export const GENERAL_ADDITIONAL_WORK_STATUSES = [
  ['draft', 'Draft'],
  ['awaiting_approval', 'Awaiting approval'],
  ['approved', 'Approved'],
  ['declined', 'Declined'],
  ['completed', 'Completed'],
  ['not_completed', 'Not completed'],
  ['voided', 'Voided'],
]

export const GENERAL_ADDITIONAL_WORK_SOURCES = [
  ['manual', 'Manual'],
  ['checklist_item', 'Checklist item'],
  ['finding', 'Finding'],
]

const BILLABLE_STATUSES = new Set(['approved', 'completed'])
const DECIDED_STATUSES = new Set(['approved', 'declined', 'completed', 'not_completed', 'voided'])
const REPORT_STATUSES = new Set(['approved', 'declined', 'completed', 'not_completed'])
const CHECKLIST_SOURCE_RESULTS = {
  pre_service_inspection: new Set(['advisory', 'action_required']),
  job_checklist: new Set(['unable_to_complete', 'additional_work_required']),
}

function text(value) {
  return String(value || '').trim()
}

export function moneyNumber(value) {
  const number = Number(value)
  return Number.isFinite(number) ? Math.max(0, Math.round(number * 100) / 100) : 0
}

export function normaliseAdditionalWorkStatus(value) {
  const key = text(value).toLowerCase()
  return GENERAL_ADDITIONAL_WORK_STATUSES.some(([status]) => status === key) ? key : 'draft'
}

export function additionalWorkStatusLabel(value) {
  const status = normaliseAdditionalWorkStatus(value)
  return GENERAL_ADDITIONAL_WORK_STATUSES.find(([key]) => key === status)?.[1] || 'Draft'
}

export function additionalWorkStatusTone(value) {
  const status = normaliseAdditionalWorkStatus(value)
  if (status === 'approved' || status === 'completed') return 'green'
  if (status === 'awaiting_approval') return 'orange'
  if (status === 'declined' || status === 'not_completed') return 'blue'
  if (status === 'voided') return 'muted'
  return 'muted'
}

export function isBillableAdditionalWorkStatus(value) {
  return BILLABLE_STATUSES.has(normaliseAdditionalWorkStatus(value))
}

export function isDecidedAdditionalWorkStatus(value) {
  return DECIDED_STATUSES.has(normaliseAdditionalWorkStatus(value))
}

export function isReportableAdditionalWorkStatus(value) {
  return REPORT_STATUSES.has(normaliseAdditionalWorkStatus(value))
}

export function canCreateAdditionalWorkFromChecklistItem(item = {}) {
  const stage = text(item.stage)
  const result = text(item.result)
  return Boolean(CHECKLIST_SOURCE_RESULTS[stage]?.has(result))
}

export function additionalWorkNeedsCustomerExplanation(row = {}) {
  const status = normaliseAdditionalWorkStatus(row.status)
  return Boolean(
    row.approval_required
    || row.customer_visible
    || status === 'awaiting_approval'
    || isReportableAdditionalWorkStatus(status)
  )
}

export function canSendAdditionalWorkApproval(row = {}) {
  return Boolean(
    row
    && normaliseAdditionalWorkStatus(row.status) === 'draft'
    && row.approval_required !== false
    && text(row.customer_explanation)
    && additionalWorkPrice(row) > 0
  )
}

export function canEditAdditionalWork(row = {}) {
  return normaliseAdditionalWorkStatus(row.status) === 'draft'
}

export function normalisePartsMaterials(value) {
  const rows = Array.isArray(value) ? value : []
  return rows
    .map((row, index) => ({
      id: text(row?.id) || `material-${index + 1}`,
      description: text(row?.description || row?.name || row?.label),
      quantity: Number.isFinite(Number(row?.quantity)) && Number(row.quantity) > 0 ? Math.round(Number(row.quantity) * 1000) / 1000 : 1,
      amount: moneyNumber(row?.amount ?? row?.price ?? row?.unit_price),
      ...(text(row?.stock_item_id) ? { stock_item_id: text(row.stock_item_id) } : {}),
    }))
    .filter(row => row.description || row.amount)
}

export function additionalWorkPrice(row = {}) {
  const labour = moneyNumber(row.labour_amount)
  const parts = moneyNumber(row.parts_amount)
  const total = moneyNumber(row.price_amount)
  return total || moneyNumber(labour + parts)
}

export function additionalWorkBillableTotal(rows = []) {
  return rows
    .filter(row => isBillableAdditionalWorkStatus(row.status))
    .reduce((sum, row) => sum + additionalWorkPrice(row), 0)
}

export function additionalWorkCustomerSafeSnapshot(rows = []) {
  return rows
    .filter(row => row && row.customer_visible !== false)
    .filter(row => isReportableAdditionalWorkStatus(row.status))
    .map(row => ({
      id: text(row.id),
      source_type: text(row.source_type) || 'manual',
      title: text(row.title),
      customer_explanation: text(row.customer_explanation),
      status: normaliseAdditionalWorkStatus(row.status),
      status_label: additionalWorkStatusLabel(row.status),
      customer_decision_at: text(row.customer_decision_at),
      customer_decision_note: text(row.customer_decision_note),
      technician_completion_comment: text(row.technician_completion_comment),
      evidence: customerSafeEvidence(row.evidence || row.linked_evidence || []),
    }))
    .filter(row => row.title && row.customer_explanation)
}

function customerSafeEvidence(rows = []) {
  return rows
    .map(row => row?.evidence || row)
    .filter(row => row && row.visibility === 'customer')
    .map(row => ({
      id: text(row.id),
      file_name: text(row.file_name),
      caption: text(row.caption),
      evidence_type: text(row.evidence_type),
      mime_type: text(row.mime_type),
      file_size: Number(row.file_size || 0),
      stage: text(row.stage),
    }))
    .filter(row => row.file_name || row.caption)
}

export function isMissingAdditionalWorkSchemaError(error) {
  const message = [
    error?.code,
    error?.message,
    error?.details,
    error?.hint,
  ].map(text).join(' ').toLowerCase()
  return message.includes('42p01')
    || message.includes('42703')
    || message.includes('42883')
    || message.includes('schema cache')
    || message.includes('could not find the table')
    || message.includes('could not find the function')
    || (message.includes('relation') && message.includes('does not exist'))
}
